// Pièces attendues par personne (F-015) : exigences selon le rôle et la situation, dépôt, remplacement,
// explication d'une absence, pièces interdites. Commun au bailleur et au candidat.
import { useState } from "react";
import { post, upload } from "@core/api";
import { useGet, useInvalidate } from "@core/hooks";
import { date } from "@core/format";
import { Badge, Button, Card, ErrorBox, FileDrop, Loading, Modal, Notice, Select, StatusChip, Textarea, useToast, type CheckStatus } from "@core/ui";
import { PieceDetail } from "./piece";

type Doc = { id: string; category: string; category_label: string; filename: string; version: number; created_at: string; fichier: string; constats?: { status: CheckStatus; title: string }[] };
type Requirement = {
  code: string;
  group_label: string;
  label: string;
  purpose: string;
  alternatives: string;
  min_count: number;
  max_count: number | null;
  optional: boolean;
  categories: { code: string; label: string; hint: string }[];
  fournies: number;
  etat: "fourni" | "explique" | "facultatif" | "manquant" | "incomplet";
  documents: Doc[];
  explication: { body: string; created_at: string } | null;
};
type PersonView = { id: string; nom: string; role_label: string; situation_label: string; visale: boolean; compte_confirme: boolean; moi: boolean; exigences: Requirement[]; autres_documents: Doc[] };
type PiecesResponse = { role: "bailleur" | "candidat"; personnes: PersonView[]; pieces_interdites: string[]; version_exigences: string; reference_legale: string };

const ETAT: Record<Requirement["etat"], [CheckStatus, string]> = {
  fourni: ["conforme", "Fournie"],
  explique: ["a_confirmer", "Explication donnée"],
  facultatif: ["info", "Facultative"],
  manquant: ["manquant", "À fournir"],
  incomplet: ["manquant", "Incomplète"],
};

function DocLine({ doc, onExamine, onReplace, canWrite }: { doc: Doc; onExamine: () => void; onReplace: (f: File) => void; canWrite: boolean }) {
  const worst = (doc.constats ?? []).find((c) => c.status === "anomalie") ?? (doc.constats ?? []).find((c) => c.status === "a_confirmer" || c.status === "non_concluant");
  return (
    <div className="controle-piece">
      <span>
        <a href={doc.fichier} target="_blank" rel="noreferrer">
          {doc.filename}
        </a>
        <span className="muted small">
          {" "}
          · {doc.category_label} · v{doc.version} · {date(doc.created_at)}
        </span>
      </span>
      <span className="row controle-cibles" style={{ flexWrap: "wrap" }}>
        {worst ? <StatusChip status={worst.status} /> : null}
        <Button variant="secondaire" small onClick={onExamine}>
          {canWrite ? "Examiner ou corriger" : "Examiner"}
        </Button>
        {canWrite ? (
          <label className="bv-btn bv-btn-discret bv-btn-petit">
            Remplacer
            <input
              type="file"
              className="sr-only"
              accept="application/pdf,image/jpeg,image/png,image/webp"
              onChange={(e) => {
                const f = e.target.files?.[0];
                if (f) onReplace(f);
                e.target.value = "";
              }}
            />
          </label>
        ) : null}
      </span>
    </div>
  );
}

function RequirementBlock({ req, person, applicationId, canWrite, onExamine, landlord }: { req: Requirement; person: PersonView; applicationId: string; canWrite: boolean; onExamine: (id: string) => void; landlord: boolean }) {
  const [category, setCategory] = useState(req.categories[0]?.code ?? "");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<unknown>(null);
  const [explaining, setExplaining] = useState(false);
  const [text, setText] = useState(req.explication?.body ?? "");
  const invalidate = useInvalidate();
  const toast = useToast();
  const [status, label] = ETAT[req.etat];
  const send = async (file: File, supersedes?: string, cat?: string) => {
    setBusy(true);
    setError(null);
    try {
      const form = new FormData();
      form.append("category", cat ?? category);
      form.append("applicant_id", person.id);
      if (supersedes) form.append("supersedes_id", supersedes);
      form.append("file", file);
      await upload(`/api/v1/applications/${applicationId}/documents`, form);
      toast(supersedes ? "Nouvelle version déposée. Les contrôles concernés sont refaits." : "Pièce déposée.");
      invalidate(`/api/v1/applications/${applicationId}`, "/api/v1/orgs/");
    } catch (e) {
      setError(e);
    } finally {
      setBusy(false);
    }
  };
  return (
    <div className="controle-exigence">
      <div className="controle-exigence-tete">
        <div>
          <p>
            <strong>{req.label}</strong>
            {req.min_count > 1 ? (
              <span className="muted small">
                {" "}
                · {req.fournies} sur {req.min_count}
              </span>
            ) : null}
          </p>
          <p className="small muted">Pourquoi : {req.purpose}</p>
        </div>
        <StatusChip status={status} label={label} />
      </div>
      {req.documents.map((d) => (
        <DocLine key={d.id} doc={d} canWrite={canWrite} onExamine={() => onExamine(d.id)} onReplace={(f) => send(f, d.id, d.category)} />
      ))}
      {req.explication ? (
        <Notice tone="info" title="Explication donnée">
          « {req.explication.body} »
        </Notice>
      ) : null}
      <ErrorBox error={error} />
      {canWrite ? (
        <div className="stack-sm">
          {req.categories.length > 1 ? <Select label="Type de pièce" value={category} options={req.categories.map((c) => ({ value: c.code, label: c.label }))} onChange={(e) => setCategory(e.target.value)} /> : null}
          <FileDrop busy={busy} label={req.documents.length ? "Ajouter une pièce" : "Déposer la pièce"} help={req.categories.find((c) => c.code === category)?.hint} onFiles={(files) => send(files[0])} />
          {req.etat !== "fourni" && !req.optional ? (
            explaining ? (
              <form
                className="stack-sm"
                onSubmit={async (e) => {
                  e.preventDefault();
                  setBusy(true);
                  setError(null);
                  try {
                    await post(`/api/v1/applications/${applicationId}/explications`, { applicant_id: person.id, requirement_code: req.code, body: text });
                    toast("Explication enregistrée.");
                    setExplaining(false);
                    invalidate(`/api/v1/applications/${applicationId}`, "/api/v1/orgs/");
                  } catch (err) {
                    setError(err);
                  } finally {
                    setBusy(false);
                  }
                }}
              >
                <Textarea label={landlord ? "Explication transmise par le candidat" : "Pourquoi cette pièce n’est pas disponible"} help={req.alternatives ? `Alternatives admissibles : ${req.alternatives}` : undefined} value={text} onChange={(e) => setText(e.target.value)} rows={3} />
                <div className="row">
                  <Button type="submit" small busy={busy}>
                    Envoyer l’explication
                  </Button>
                  <Button variant="discret" small onClick={() => setExplaining(false)}>
                    Annuler
                  </Button>
                </div>
              </form>
            ) : (
              <div className="controle-cibles">
                <Button variant="discret" small onClick={() => setExplaining(true)}>
                  {landlord ? "Noter une explication du candidat" : person.moi ? "Je n’ai pas cette pièce" : "Cette pièce n’est pas disponible"}
                </Button>
              </div>
            )
          ) : null}
        </div>
      ) : req.alternatives && req.etat !== "fourni" ? (
        <p className="small muted">Alternatives admissibles : {req.alternatives}</p>
      ) : null}
    </div>
  );
}

export function RequirementsView({ applicationId, orgId, canWrite }: { applicationId: string; orgId?: string; canWrite: boolean }) {
  const q = useGet<PiecesResponse>(`/api/v1/applications/${applicationId}/pieces`);
  const [open, setOpen] = useState<string | null>(null);
  if (q.isLoading) return <Loading />;
  if (q.error) return <ErrorBox error={q.error} />;
  const d = q.data!;
  return (
    <div className="stack">
      {d.personnes.map((p) => (
        <Card
          key={p.id}
          title={
            <span>
              {p.nom} <span className="muted small">· {p.role_label} · {p.situation_label}</span>
            </span>
          }
          actions={
            <span className="row">
              {p.visale ? <Badge tone="encre">Visa Visale annoncé</Badge> : null}
              {!p.compte_confirme ? <Badge tone="attention">Informations à confirmer par la personne</Badge> : null}
            </span>
          }
        >
          {p.exigences.map((r) => (
            <RequirementBlock key={r.code} req={r} person={p} applicationId={applicationId} canWrite={canWrite} onExamine={setOpen} landlord={d.role === "bailleur"} />
          ))}
          {p.autres_documents.length ? (
            <div className="stack-sm">
              <h3>Autres pièces</h3>
              {p.autres_documents.map((doc) => (
                <DocLine key={doc.id} doc={doc} canWrite={false} onExamine={() => setOpen(doc.id)} onReplace={() => undefined} />
              ))}
            </div>
          ) : null}
        </Card>
      ))}
      <Card title="Pièces qui ne peuvent pas être demandées">
        <p className="small muted">{d.reference_legale}. Liste des exigences : version {d.version_exigences}.</p>
        <ul>
          {d.pieces_interdites.map((x) => (
            <li key={x}>{x}</li>
          ))}
        </ul>
      </Card>
      <Modal open={!!open} onClose={() => setOpen(null)} title="Examen de la pièce">
        {open ? <PieceDetail applicationId={applicationId} docId={open} orgId={orgId} canWrite={canWrite} /> : null}
      </Modal>
    </div>
  );
}
